import { type ReactNode, useCallback, useState } from 'react';

import { Box } from '@mui/material';
import { useLocation } from 'react-router';

import { GlobalLoadingOverlay } from '@/components/common/GlobalLoadingOverlay';

import { Header } from './Header';
import { HeaderBreadcrumbProvider } from './HeaderBreadcrumbContext';
import { Sidebar } from './Sidebar';

interface AppShellProps {
  children: ReactNode;
  /** Hide sidebar on certain routes */
  hideSidebar?: boolean;
  /** Current app ID for navigation */
  appId?: string;
  /** Current tenant ID for team count */
  tenantId?: string;
  /** Current tenant info for breadcrumb */
  tenantName?: string;
  /** Current app name for breadcrumb */
  appName?: string;
  /** Header color override (based on tenant edition) */
  headerColor?: string;
}

const HEADER_HEIGHT = 50;

/**
 * Main application shell with header and sidebar
 * Sidebar is always collapsed by default and expands on hover
 */
export const AppShell = ({
  children,
  hideSidebar = false,
  appId,
  tenantId,
  tenantName,
  appName,
  headerColor,
}: AppShellProps) => {
  const location = useLocation();
  const isRecipeEditor =
    location.pathname.includes('/tf/') && location.pathname.split('/').length > 4;

  // Sidebar always starts collapsed, expands on hover
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSidebarToggle = useCallback(() => {
    // Toggle between collapsed and expanded
    setSidebarOpen((prev) => !prev);
  }, []);

  const handleSidebarHover = useCallback((hovering: boolean) => {
    // Always expand on hover, collapse on leave
    setSidebarOpen(hovering);
  }, []);

  const showSidebar = !hideSidebar && !!appId;

  return (
    <HeaderBreadcrumbProvider>
      {/* Global Loading Overlay */}
      <GlobalLoadingOverlay />

      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {/* Fixed Header */}
        <Header
          color={headerColor}
          tenantName={tenantName}
          tenantId={tenantId}
          appName={appName}
          appId={appId}
          onMenuClick={showSidebar ? handleSidebarToggle : undefined}
        />

        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            pt: `${HEADER_HEIGHT}px`,
            ...(isRecipeEditor
              ? {
                  height: `calc(100vh - ${HEADER_HEIGHT}px)`,
                  overflow: 'hidden',
                }
              : {
                  minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                }),
          }}
        >
          {/* Sidebar */}
          {showSidebar && tenantId && (
            <Sidebar
              open={sidebarOpen}
              appId={appId}
              tenantId={tenantId}
              onHover={handleSidebarHover}
            />
          )}

          {/* Main Content Area */}
          <Box
            component="main"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: isRecipeEditor ? 'background.paper' : 'background.default',
              minHeight: 0,
              minWidth: 0,
              overflow: isRecipeEditor ? 'hidden' : 'auto',
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </HeaderBreadcrumbProvider>
  );
};
